import {MiniEvent} from "@cdx/common";
import {throttle} from "es-toolkit/compat";
import cdxEnv from "../env";
import {postRequest} from "./request";
import messenger from "./messenger";
import {MutationRefusal} from "./mutation-refusal";

type Frame = {fileName?: string; lineNumber?: number; columnNumber?: number; functionName?: string};

declare global {
  interface Window {
    /** Set by an inline script in index.html; returns the events it held and stops holding more. */
    __cdxTakeEarlyErrors?: () => Event[];
  }
}

export const exceptionEvent = new MiniEvent<void>();

// A failed report must not become an unhandled rejection, which would be reported in turn.
const throttledRequest = throttle(({stackFrames, msg}: {stackFrames: Frame[]; msg: string}) => {
  postRequest("/services/send-error", {
    stackFrames,
    message: msg,
    appVersion: cdxEnv.DEPLOYMENT_LABEL,
  }).catch((e) => console.error("failed to send error", e));
}, 1000);

// A frame-less report still groups by its message; rethrowing here would only make `window.onerror`
// report "Cannot parse given Error object" instead. The parser is loaded with the first report, and
// a failed load is treated like a failed parse.
const parseStackFrames = async (error: Error): Promise<Frame[]> => {
  try {
    const {default: ErrorStackParser} = await import("error-stack-parser");
    return ErrorStackParser.parse(error);
  } catch {
    return [];
  }
};

// `JSON.stringify` turns an `Error` into `{}`, so `{type: "fetchError", error}` would lose what failed.
const errorReplacer = (_key: string, v: unknown) =>
  v instanceof Error ? `${v.name}: ${v.message}` : v;

// `JSON.stringify` alone turns most non-errors into `{}`, so name the type too. It also throws on
// cycles and returns undefined for `undefined`.
const describeNonError = (value: unknown) => {
  const getType = () => {
    if (value === null || value === undefined) return String(value);
    return (value as object).constructor?.name || typeof value;
  };
  const getJson = () => {
    try {
      return JSON.stringify(value, errorReplacer) ?? String(value);
    } catch {
      return String(value);
    }
  };
  return `${getType()} ${getJson()}`;
};

export const postError = (error: Error, msg: string) => {
  void parseStackFrames(error).then((stackFrames) => throttledRequest({stackFrames, msg}));
};

const reportUncaught: OnErrorEventHandlerNonNull = (event, file, line, col, errorArg) => {
  const msg = typeof event === "string" ? event : event.type;
  if (msg === "Script error.") return;
  if (msg.indexOf("ResizeObserver loop") >= 0) return;
  if (msg.indexOf("Minified exception occurred") >= 0) return;
  if (msg.indexOf("Cannot read property '_currentElement'") >= 0) return;
  setTimeout(() => {
    exceptionEvent.emit();
  });
  // typed as `Error`, but it's whatever was thrown
  const error: unknown = errorArg;
  const fallbackFrames: Frame[] = [
    {fileName: file, lineNumber: line, columnNumber: col, functionName: "unknown"},
  ];
  if (error instanceof Error) {
    void parseStackFrames(error).then((stackFrames) =>
      throttledRequest({stackFrames: stackFrames.length ? stackFrames : fallbackFrames, msg})
    );
  } else if (error !== undefined && error !== null) {
    // `throw {...}` arrives as "Uncaught [object Object]"
    throttledRequest({stackFrames: fallbackFrames, msg: `uncaught ${describeNonError(error)}`});
  } else {
    throttledRequest({stackFrames: fallbackFrames, msg});
  }
};

const reportUnhandledRejection = (event: PromiseRejectionEvent) => {
  const reason: unknown = event.reason;
  // A call site that fires a mutation without a catch; the server's answer is meant for the user.
  if (reason instanceof MutationRefusal) {
    event.preventDefault();
    if (!reason.shown) messenger.send(reason.message, {type: "error"});
    return;
  }
  if (reason instanceof Error) {
    void parseStackFrames(reason).then((stackFrames) =>
      throttledRequest({stackFrames, msg: `unhandled rejection: ${reason.message}`})
    );
  } else {
    throttledRequest({stackFrames: [], msg: `unhandled rejection: ${describeNonError(reason)}`});
  }
};

window.onerror = reportUncaught;
window.addEventListener("unhandledrejection", reportUnhandledRejection);

for (const event of window.__cdxTakeEarlyErrors?.() ?? []) {
  if (event instanceof PromiseRejectionEvent) {
    reportUnhandledRejection(event);
  } else if (event instanceof ErrorEvent) {
    reportUncaught(event.message, event.filename, event.lineno, event.colno, event.error);
  }
}
